import type { Voucher } from '@/entities/voucher/@x/gift'
import { safeLocalStorage } from '@/shared/lib'
import type { Gift, GiftOpening } from './types'

/**
 * Офлайн-ваучер: действующий ваучер получателя хранится на устройстве, чтобы
 * QR открывался на кассе без сети и после перезапуска Mini App (решение
 * пользователя 02.10.2026, итерация 7; CLAUDE.md, «Экран ваучера»).
 *
 * - **Только `localStorage` нашего домена.** Ни CloudStorage / SecureStorage
 *   Telegram, ни аналитика, ни внешние сервисы. Содержимое не логируется.
 * - **Только поля, нужные экрану ваучера**: партнёр, номинал, тексты продукта,
 *   записка дарителя, код и QR, точки. Без чека, дат, картинок, имени получателя.
 * - **На Telegram ID.** Ключ — `tapgift.vouchers.v1.<telegramId>`. При запуске
 *   под другим пользователем чужие записи стираются целиком; Telegram ID
 *   неизвестен — стирается всё и ничего не пишется.
 * - **Только то, что действует сейчас**: ответ бэкенда `view: RECEIVER` и
 *   статус `CLAIMED` — после «Принять» (в `OPENED` кода нет, и записи нет).
 *   Любой другой ответ по подарку — ждёт решения, погашен, возвращён,
 *   заблокирован, недоступен, «чужой» — запись удаляет.
 * - **Находится и по токену отправки** (BE-53): кнопка бота «Открыть подарок»
 *   ведёт на `s_<token>`, а без сети узнать по токену id подарка не у кого.
 *   Токены отправки — те же, что в карточке в чате, секрета в них нет.
 *
 * Хранилище недоступно (приватный режим, запрет данных в iframe web-клиента) —
 * всё работает как раньше: ваучер живёт в памяти, пока Mini App открыт.
 */

const KEY_PREFIX = 'tapgift.vouchers.'

/** Версия формы записи: поменялась форма — старые записи не подсовываются экрану. */
const STORAGE_VERSION = 'v1'

/** Больше действующих ваучеров у одного человека не бывает; лишние — самые давние. */
const MAX_ENTRIES = 30

/** Токенов на подарок: новая отправка — новый токен, но получатель у подарка один (BE-53). */
const MAX_TOKENS = 3

type StoredGift = Pick<Gift, 'id' | 'amount' | 'fromName' | 'isAnonymous' | 'message' | 'locations'> & {
  brand: Pick<Gift['brand'], 'id' | 'name'>
  product: Pick<Gift['product'], 'id' | 'name' | 'redemptionInstruction' | 'terms'>
  voucher: Voucher
}

type StoredEntry = {
  /** Когда бэкенд последний раз подтвердил, что ваучер действует. */
  checkedAt: number
  gift: StoredGift
  /** Токены отправки, по которым открывали этот подарок (`s_<token>`); у записей до BE-53 их нет. */
  tokens?: string[]
}

type Store = Record<string, StoredEntry>

/** Ключ текущего пользователя; `null` — хранить нечего и некуда. */
let storageKey: string | null = null

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isString = (value: unknown): value is string => typeof value === 'string'

/** Битая запись (ручная правка, сбой записи) не должна ронять экран — она просто пропускается. */
const isStoredEntry = (value: unknown): value is StoredEntry => {
  if (!isRecord(value) || typeof value.checkedAt !== 'number' || !isRecord(value.gift)) {
    return false
  }

  const { gift, tokens } = value
  const { brand, product, voucher } = gift

  return (
    (tokens === undefined || (Array.isArray(tokens) && tokens.every(isString))) &&
    isString(gift.id) &&
    typeof gift.amount === 'number' &&
    typeof gift.isAnonymous === 'boolean' &&
    Array.isArray(gift.locations) &&
    isRecord(brand) &&
    isString(brand.name) &&
    isRecord(product) &&
    isString(product.name) &&
    isString(product.redemptionInstruction) &&
    isString(product.terms) &&
    isRecord(voucher) &&
    isString(voucher.code) &&
    isString(voucher.qrToken)
  )
}

const readStore = (): Store => {
  if (!storageKey) {
    return {}
  }

  const raw = safeLocalStorage.getItem(storageKey)

  if (!raw) {
    return {}
  }

  try {
    const parsed: unknown = JSON.parse(raw)

    if (!isRecord(parsed)) {
      return {}
    }

    return Object.fromEntries(Object.entries(parsed).filter((entry): entry is [string, StoredEntry] => isStoredEntry(entry[1])))
  } catch {
    return {}
  }
}

const writeStore = (store: Store): void => {
  if (!storageKey) {
    return
  }

  // Свежие первыми. `sort` по месту, а не `toSorted`: его нет в WebView старше Chrome 110.
  const entries = Object.entries(store)

  entries.sort(([, a], [, b]) => b.checkedAt - a.checkedAt)

  if (entries.length === 0) {
    safeLocalStorage.removeItem(storageKey)
  } else {
    safeLocalStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(entries.slice(0, MAX_ENTRIES))))
  }
}

const withoutEntry = (store: Store, giftId: string): Store =>
  Object.fromEntries(Object.entries(store).filter(([id]) => id !== giftId))

/** Действующий ваучер этого зрителя — то, что можно показать без сети. */
const pickStoredGift = (opening: GiftOpening): StoredGift | null => {
  const { gift } = opening

  if (opening.view !== 'RECEIVER' || !gift || gift.status !== 'CLAIMED' || !gift.voucher) {
    return null
  }

  return {
    id: gift.id,
    amount: gift.amount,
    brand: { id: gift.brand.id, name: gift.brand.name },
    product: {
      id: gift.product.id,
      name: gift.product.name,
      redemptionInstruction: gift.product.redemptionInstruction,
      terms: gift.product.terms,
    },
    fromName: gift.fromName,
    isAnonymous: gift.isAnonymous,
    message: gift.message,
    voucher: { code: gift.voucher.code, qrToken: gift.voucher.qrToken },
    locations: gift.locations,
  }
}

/**
 * Карточка для экрана из записи. Полей, которых экрану ваучера не нужно, в
 * записи нет — здесь они пустые; ответ бэкенда заменит карточку целиком.
 */
const toOpening = (stored: StoredGift): GiftOpening => ({
  view: 'RECEIVER',
  teaser: null,
  gift: {
    ...stored,
    status: 'CLAIMED',
    brand: { ...stored.brand, logoUrl: '' },
    product: { ...stored.product, imageUrl: '' },
    recipientName: null,
    // Оформление коробки экрану ваучера не нужно; `CORAL` — единственное, что сейчас отправляется.
    boxTheme: 'CORAL',
    receipt: null,
    paidAt: null,
    inlineSentAt: null,
    openedAt: null,
    decisionDeadlineAt: null,
    claimedAt: null,
    redeemedAt: null,
    redeemedLocation: null,
    // Отметку об отмене погашения хранит бэкенд, а не устройство: «показать один раз» держится на нём.
    redemptionCancellableUntil: null,
    redemptionCancelledAt: null,
    lastSendEnd: null,
    refundedAt: null,
    orderNumber: null,
    createdAt: '',
  },
})

/**
 * Привязать хранилище к пользователю — один раз при старте, до первого
 * запроса подарка. Записи всех остальных пользователей (и старых версий
 * формы) стираются.
 */
export const bindOfflineVouchers = (telegramId: number | null): void => {
  storageKey = telegramId === null ? null : `${KEY_PREFIX}${STORAGE_VERSION}.${telegramId}`

  for (const key of safeLocalStorage.keys()) {
    if (key.startsWith(KEY_PREFIX) && key !== storageKey) {
      safeLocalStorage.removeItem(key)
    }
  }
}

/** Сохранённый ваучер подарка — карточкой в форме ответа бэкенда — и когда он подтверждён. */
export const readOfflineVoucher = (giftId: string): { opening: GiftOpening; checkedAt: number } | null => {
  const entry = readStore()[giftId]

  return entry ? { opening: toOpening(entry.gift), checkedAt: entry.checkedAt } : null
}

/** Подарок, открытый по этому токену отправки, — id и запись. */
const findBySend = (store: Store, token: string): [string, StoredEntry] | null =>
  Object.entries(store).find(([, entry]) => entry.tokens?.includes(token) === true) ?? null

/** Сохранённый ваучер, открытый раньше по этому токену отправки: вход по `s_<token>` без сети. */
export const readOfflineVoucherBySend = (
  token: string,
): { giftId: string; opening: GiftOpening; checkedAt: number } | null => {
  const found = findBySend(readStore(), token)

  return found ? { giftId: found[0], opening: toOpening(found[1].gift), checkedAt: found[1].checkedAt } : null
}

/**
 * Свежий ответ бэкенда по подарку: ваучер действует — запись обновляется
 * (`token` — если подарок открыт по токену отправки, он запоминается), иначе
 * удаляется.
 */
export const syncOfflineVoucher = (giftId: string, opening: GiftOpening, token?: string): void => {
  if (!storageKey) {
    return
  }

  const stored = pickStoredGift(opening)
  const store = readStore()

  if (stored) {
    const known = store[giftId]?.tokens ?? []
    const tokens = token && !known.includes(token) ? [token, ...known].slice(0, MAX_TOKENS) : known

    writeStore({ ...store, [giftId]: { checkedAt: Date.now(), gift: stored, ...(tokens.length > 0 ? { tokens } : {}) } })
  } else if (giftId in store) {
    writeStore(withoutEntry(store, giftId))
  }
}

/**
 * Ответ по токену без карточки (`NOT_FOUND`, `GONE`, `BLOCKED`…): если по
 * этому токену открывали сохранённый ваучер, запись удаляется — как и по любому
 * другому ответу, кроме действующего ваучера.
 */
export const forgetOfflineVoucherBySend = (token: string): void => {
  const store = readStore()
  const found = findBySend(store, token)

  if (found) {
    writeStore(withoutEntry(store, found[0]))
  }
}

/** Подарок недоступен этому человеку (`GF-001`, `GF-003`) — ваучер с устройства удаляется. */
export const forgetOfflineVoucher = (giftId: string): void => {
  const store = readStore()

  if (giftId in store) {
    writeStore(withoutEntry(store, giftId))
  }
}

/** Подарки, которые бэкенд давно не подтверждал, — самые давние первыми. */
export const listStaleOfflineVouchers = (olderThanMs: number, limit: number): string[] => {
  const threshold = Date.now() - olderThanMs

  const stale = Object.entries(readStore()).filter(([, entry]) => entry.checkedAt < threshold)

  stale.sort(([, a], [, b]) => a.checkedAt - b.checkedAt)

  return stale.slice(0, limit).map(([giftId]) => giftId)
}
